import { GqlField, GqlGroup, MatterField } from "../../types";
import {
  ContactFieldCandidate,
  ContactType,
  findDuplicateContactFields,
  findExistingContactDetailsGroup,
  findRoleContactFields,
  getDefaultContactDetailsLabel,
  getLabelText,
} from "../../utils";

export type DuplicateContactWarning = {
  boldText: string;
  suffix: string;
  tooltipText?: string;
};

const singular = " duplicated. Remove it from the form.";
const plural = " duplicated. Remove them from the form.";

const joinLabels = (labels: string[]) =>
  labels.length > 1
    ? `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`
    : labels.join("");

const buildWarning = (
  labels: string[],
): DuplicateContactWarning | undefined => {
  if (labels.length === 1) return { boldText: labels[0], suffix: singular };
  if (labels.length > 1)
    return {
      boldText: `${labels.length} fields`,
      suffix: plural,
      tooltipText: joinLabels(labels),
    };
  return undefined;
};

const existingGroupWarning = ({
  layoutId,
  contactType,
  roleDisplayName,
  formGroups,
}: {
  layoutId: string | undefined;
  contactType: ContactType;
  roleDisplayName: string;
  formGroups: GqlGroup[];
}): DuplicateContactWarning | undefined => {
  const existingGroup = findExistingContactDetailsGroup({
    groups: formGroups,
    layoutId,
    contactType,
  });
  if (!existingGroup) return undefined;

  return {
    boldText:
      existingGroup.values.label ||
      getDefaultContactDetailsLabel(roleDisplayName),
    suffix: singular,
  };
};

// blocked by anything already mapped to the role
export const getRoleContactWarning = ({
  layoutId,
  contactType,
  roleDisplayName,
  matterFields,
  formFields,
  formGroups,
}: {
  layoutId: string | undefined;
  contactType: ContactType;
  roleDisplayName: string;
  matterFields: MatterField[];
  formFields: GqlField[];
  formGroups: GqlGroup[];
}): DuplicateContactWarning | undefined => {
  const seen = new Set<string>();
  const labels: string[] = [];

  findRoleContactFields({ fields: formFields, layoutId, contactType }).forEach(
    ({ values }) => {
      if (!values.field || seen.has(values.field.name)) return;
      seen.add(values.field.name);
      // matter field has the details prefix getLabelText strips
      const loaded = matterFields.find((f) => f.name === values.field!.name);
      labels.push(getLabelText(loaded || values.field));
    },
  );

  return (
    buildWarning(labels) ||
    existingGroupWarning({ layoutId, contactType, roleDisplayName, formGroups })
  );
};

// saved sets match on the fields they add
export const getDuplicateContactWarning = ({
  candidates,
  layoutId,
  contactType,
  roleDisplayName,
  formFields,
  formGroups,
}: {
  candidates: ContactFieldCandidate[];
  layoutId: string | undefined;
  contactType: ContactType;
  roleDisplayName: string;
  formFields: GqlField[];
  formGroups: GqlGroup[];
}): DuplicateContactWarning | undefined => {
  const duplicates = findDuplicateContactFields({
    candidates,
    fields: formFields,
  });

  return (
    buildWarning(duplicates.map((duplicate) => duplicate.label)) ||
    existingGroupWarning({ layoutId, contactType, roleDisplayName, formGroups })
  );
};
